import type { SearchGroup } from '@/api/types'

/** Sentinel for "direct messages only" on the wire — never a real channel id. */
export const DIRECT_ONLY = 'none'

/**
 * Would jumping to a search hit land OUTSIDE the currently filtered history window?
 *
 * Search is deliberately zone-wide: `GET /search` takes no channel, so with `#deploys` selected a
 * term that only ever appeared in `#watercooler` is still listed, and clicking it is a jump into a
 * message the filtered page can never render.
 *
 * That used to fail silently and badly. `jumpTo` finds no loaded row for the hit, re-anchors the
 * window with `setAnchor(seq - 1)` — still channel-filtered — and sets `activeSeq` to a row that
 * will never exist. The reader's rail moves somewhere unrelated, the highlighted message is absent,
 * and nothing says why. This codebase has already recorded that failure once, as "the rail was not
 * a navigation control".
 *
 * So the click widens the view instead of pretending. Answering `true` here makes `MessagesView`
 * clear the channel filter before anchoring, which is the cheapest thing that makes the click land
 * somewhere true. The alternative — threading `channel` through search — would need a new query
 * parameter and a filtered `tsvector` query on the server, and it would make the hit disappear from
 * the results entirely rather than explaining itself, which is the same silence in a different place.
 *
 * @param filter the active `?channel=` value: `undefined` (everything), {@link DIRECT_ONLY}, or a
 *     channel id
 * @param filteredChannelName the name of the channel `filter` identifies, or `undefined` while the
 *     channel list is still loading or when the filter names a channel that no longer exists. A hit
 *     is then treated as outside the window: clearing a filter unnecessarily still lands the jump,
 *     whereas keeping one we cannot verify is exactly the dead click this exists to remove.
 * @param group the search result group the clicked hit belongs to
 */
export function jumpLeavesTheFilteredView(
  filter: string | undefined,
  filteredChannelName: string | undefined,
  group: Pick<SearchGroup, 'kind' | 'label'>,
): boolean {
  if (filter === undefined) return false
  if (filter === DIRECT_ONLY) return group.kind === 'CHANNEL'
  return group.kind !== 'CHANNEL' || group.label !== filteredChannelName
}
